import React from 'react';
import { useDeepMemo } from '@beeline.com/bp-design-system';
import { CustomFieldDefinition, CustomFieldDefinitionColumn } from '../utils/CustomFields.types';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { CustomFieldDefinitionActions } from './CustomFieldDefinitionActions';
import { CustomFieldStatus } from './CustomFieldStatus';
import { CheckedIcon } from './CheckedIcon';

const MAX_STR_LENGTH = 50;

export const useCustomFieldDefinitionsColumns = ({
  onSubmitDone,
  onEdit,
  onDelete
}: {
  onSubmitDone: () => void;
  onEdit: (customDefinition: CustomFieldDefinition) => void;
  onDelete: (customDefinition: CustomFieldDefinition) => void;
}): CustomFieldDefinitionColumn[] => {
  const intl = useCustomFieldsAndObjectsIntl();
  return useDeepMemo(
    (): CustomFieldDefinitionColumn[] =>
      (
        [
          {
            Header: intl.nameLabel,
            defaultWidth: 250,
            id: 'name',
            blockColumnHiding: true,
            accessor: (row) => <Name row={row} />
          },
          {
            Header: intl.status,
            id: 'status',
            minWidth: 120,
            accessor: (row) => <CustomFieldStatus status={row.status} />
          },
          {
            Header: intl.entityType,
            minWidth: 120,
            defaultWidth: 200,
            accessor: 'parentEntityType',
            Cell: ({ value }) => (value ? value : '-')
          },
          {
            Header: intl.codeLabel,
            minWidth: 120,
            accessor: 'code',
            Cell: ({ value }) => value ?? '-'
          },
          {
            Header: intl.groupLabel,
            minWidth: 120,
            accessor: 'group',
            Cell: ({ value }) => value?.name ?? '-'
          },
          {
            Header: intl.helperTextLabel,
            accessor: 'helperText',
            Cell: ({ value }) => value ?? '-'
          },
          {
            Header: intl.typeLabel,
            minWidth: 120,
            accessor: 'dataType',
            Cell: ({ value }) => value ?? '-'
          },
          {
            Header: intl.valuesLabel,
            accessor: 'values',
            minWidth: 120,
            Cell: ({ value: values }) => {
              if (values?.length) {
                const summaryStr = values?.map((value) => value?.label).join(', ');
                if (summaryStr.length > MAX_STR_LENGTH) {
                  return `${summaryStr.slice(0, MAX_STR_LENGTH)}...`;
                }

                return summaryStr;
              }

              return '-';
            }
          },
          {
            Header: intl.objectLabel,
            minWidth: 140,
            accessor: 'customObjectDefinition',
            Cell: ({ value, row }) => (row.original.dataType === 'OBJECT' ? value?.name : '-')
          },
          {
            Header: intl.multivaluedLabel,
            minWidth: 110,
            accessor: 'multivalued',
            Cell: ({ value }) => <CheckedIcon dataTest="multi-valued-cell" checked={!!value} />
          },
          {
            Header: '',
            accessor: (row) => (
              <CustomFieldDefinitionActions
                customDefinition={row}
                onSubmitDone={onSubmitDone}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ),
            id: '[menu]',
            sticky: 'right'
          }
        ] as (CustomFieldDefinitionColumn | undefined)[]
      ).filter((item) => !!item) as CustomFieldDefinitionColumn[],
    [
      intl.nameLabel,
      intl.status,
      intl.entityType,
      intl.codeLabel,
      intl.groupLabel,
      intl.helperTextLabel,
      intl.typeLabel,
      intl.valuesLabel,
      intl.objectLabel,
      intl.multivaluedLabel,
      onSubmitDone,
      onEdit,
      onDelete
    ]
  );
};

// It is needed to detect status of custom field definition
const Name: React.FC<React.PropsWithChildren<{ row: CustomFieldDefinition }>> = ({ row }) => <>{row.name}</>;
